import { Button, IButtonProps } from 'Components/Core';
import { withInjectedProps } from 'Components/Core/InjectedPropsContext';
import { WorksheetActionByDialog } from 'Components/Worksheets/Core/WorksheetActionBySection';
import { actions as noticeActions } from 'Data/ReduxViewModels/Notices';
import { actions } from 'Data/ReduxViewModels/WorksheetBatchUpdate';
import { hasValue } from 'Lib/Any';
import { useHasChangedAfterFirstLoad } from 'Lib/Hooks';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

type WorksheetBatchUpdateCheckButtonProps = IButtonProps;

/**
 * Button that can be used to check the selected worksheets
 */
let WorksheetBatchUpdateCheckButton: React.FC<WorksheetBatchUpdateCheckButtonProps> = ({ disabled, ...otherProps }) => {
  const dispatch = useDispatch();

  // NOTE: Currently worksheets can only be loaded for the same work order, so they are always in the same lab context.
  const laboratoryId = useSelector(
    (state) =>
      Object.values(state.worksheetBatchUpdate.worksheets).find((_) => true)?.syncModel_synced.header.Laboratory.Id
  );
  // NOTE: We currently need to load people in a worksheet context (but this only checks against worksheet location).
  // As above, all worksheets will be loaded for the same work order so the people available shoudln't vary by worksheet.
  const worksheetId = useSelector((state) => Object.keys(state.worksheetBatchUpdate.worksheets).find((_) => true));

  const canCheck = useSelector(
    (state) =>
      state.worksheetBatchUpdate.status.initialised &&
      state.worksheetBatchUpdate.status.queryReady &&
      !state.worksheetBatchUpdate.status.refreshingWorksheets &&
      !state.worksheetBatchUpdate.status.configuring &&
      !state.worksheetBatchUpdate.status.applyingUpdate &&
      Object.values(state.worksheetBatchUpdate.worksheetsState).some((w) => w.selected)
  );
  const checking = useSelector((state) => state.worksheetBatchUpdate.status.checking);

  const [showCheckedSelectionDialog, setShowCheckedSelectionDialog] = useState<boolean>(false);

  const hasCheckingChanged = useHasChangedAfterFirstLoad(checking);

  // Auto-close dialog when checking finishes
  useEffect(() => {
    if (hasCheckingChanged && !checking && showCheckedSelectionDialog) setShowCheckedSelectionDialog(false);
  }, [checking, hasCheckingChanged, showCheckedSelectionDialog, setShowCheckedSelectionDialog]);

  return (
    <>
      {worksheetId && (
        <WorksheetActionByDialog
          title={'Set Checker and Date'}
          actionByLabel="Checked By"
          actionDateLabel="Date Checked"
          laboratoryId={laboratoryId}
          worksheetId={worksheetId!}
          busy={checking}
          visible={showCheckedSelectionDialog}
          onSetActionValues={(d) =>
            hasValue(d.actionBy?.Id) && hasValue(d.actionDate)
              ? dispatch(
                  actions.checkSelectedWorksheets({
                    checkedByPerson: d.actionBy!,
                    checkedDate: d.actionDate!,
                  })
                )
              : dispatch(
                  noticeActions.addNotice({
                    message: 'You must set Checked By and Date Checked to check the selected worksheets.',
                    severity: 'error',
                  })
                )
          }
          onClose={() => setShowCheckedSelectionDialog(false)}
        />
      )}
      <Button
        primary
        onClick={() => setShowCheckedSelectionDialog(true)}
        loading={checking}
        disabled={disabled || !canCheck}
        {...otherProps}
      >
        Check Selected
      </Button>
    </>
  );
};

WorksheetBatchUpdateCheckButton = withInjectedProps(WorksheetBatchUpdateCheckButton);

export default WorksheetBatchUpdateCheckButton;
export { WorksheetBatchUpdateCheckButton };
